import React, { FC, useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import * as yup from "yup";
import { useFormik } from "formik";
import {
  Button,
  Input,
  Toggle,
} from "../../../../components";
import { useUi } from "../../../../context/ui.context";
import { projectService } from "../../../../api";
import { extractErrorMessage } from "../../../../utils/error-extractor";
import {
  TabContent,
  FormSection,
  FormRow,
  FormActions,
  QuotaGrid,
} from "./styles";

type ComputeQuotasTabProps = {
  projectId: string;
  regionId: string;
};

const ComputeQuotasTab: FC<ComputeQuotasTabProps> = ({ projectId, regionId }) => {
  const [t] = useTranslation();
  const { viewAlert } = useUi();

  const [loading, setLoading] = useState<boolean>(true);
  const [updateLoading, setUpdateLoading] = useState<boolean>(false);

  const validationSchema = yup.object().shape({
    instances: yup.number().min(-1, t("INVALID_VALUE")).required(t("REQUIRED_FIELD")),
    cores: yup.number().min(-1, t("INVALID_VALUE")).required(t("REQUIRED_FIELD")),
    ram: yup.number().min(-1, t("INVALID_VALUE")).required(t("REQUIRED_FIELD")),
    key_pairs: yup.number().min(-1, t("INVALID_VALUE")).required(t("REQUIRED_FIELD")),
    metadata_items: yup.number().min(-1, t("INVALID_VALUE")).required(t("REQUIRED_FIELD")),
    server_groups: yup.number().min(-1, t("INVALID_VALUE")).required(t("REQUIRED_FIELD")),
    server_group_members: yup.number().min(-1, t("INVALID_VALUE")).required(t("REQUIRED_FIELD")),
  });

  const formik = useFormik({
    enableReinitialize: true,
    validationSchema,
    initialValues: {
      instances: -1,
      cores: -1,
      ram: -1,
      key_pairs: -1,
      metadata_items: -1,
      server_groups: -1,
      server_group_members: -1,
      force: false,
      set_for_all_regions: false,
    },
    onSubmit: (formValues) => {
      setUpdateLoading(true);

      const payload = {
        ...formValues,
        region_id: regionId,
      };

      projectService
        .setComputeQuotas(projectId, payload)
        .then(() => {
          viewAlert({
            severity: "information",
            message: t("QUOTAS_UPDATED_SUCCESSFULLY"),
          });
          loadQuotas();
        })
        .catch((error) => {
          const message =
            error?.response?.data?.detail ||
            extractErrorMessage(error?.response?.data) ||
            t("FAILED_TO_UPDATE_QUOTAS");
          viewAlert({
            severity: "error",
            message,
          });
        })
        .finally(() => {
          setUpdateLoading(false);
        });
    },
  });

  const {
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
    setFieldValue,
    setValues,
  } = formik;

  const loadQuotas = useCallback(() => {
    setLoading(true);
    projectService
      .getComputeQuotas(projectId, regionId)
      .then((res) => {
        const quotas = res.data;
        setValues({
          instances: quotas.instances ?? -1,
          cores: quotas.cores ?? -1,
          ram: quotas.ram ?? -1,
          key_pairs: quotas.key_pairs ?? -1,
          metadata_items: quotas.metadata_items ?? -1,
          server_groups: quotas.server_groups ?? -1,
          server_group_members: quotas.server_group_members ?? -1,
          force: false,
          set_for_all_regions: false,
        });
      })
      .catch((error) => {
        const message =
          error?.response?.data?.detail ||
          extractErrorMessage(error?.response?.data) ||
          t("FAILED_TO_LOAD_QUOTAS");
        viewAlert({
          severity: "error",
          message,
        });
      })
      .finally(() => {
        setLoading(false);
      });
  }, [projectId, regionId, setValues, t, viewAlert]);

  useEffect(() => {
    loadQuotas();
  }, [loadQuotas]);

  const onFormSubmit = useCallback(
    (e?: React.FormEvent) => {
      e?.preventDefault();
      formik.submitForm();
    },
    [formik]
  );

  const formikProps = {
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
  };

  if (loading) {
    return (
      <TabContent>
        <p>{t("LOADING")}</p>
      </TabContent>
    );
  }

  return (
    <TabContent>
      <FormSection as="form" onSubmit={onFormSubmit}>
        <QuotaGrid>
          <FormRow>
            <Input
              required
              title={t("INSTANCES")}
              placeholder="-1"
              fieldName="instances"
              type="number"
              toolTip={true}
              toolTipText={t("QUOTA_UNLIMITED_HINT")}
              {...formikProps}
            />
          </FormRow>

          <FormRow>
            <Input
              required
              title={t("VCPUS_CORES")}
              placeholder="-1"
              fieldName="cores"
              type="number"
              toolTip={true}
              toolTipText={t("QUOTA_UNLIMITED_HINT")}
              {...formikProps}
            />
          </FormRow>

          <FormRow>
            <Input
              required
              title={t("RAM_MB")}
              placeholder="-1"
              fieldName="ram"
              type="number"
              toolTip={true}
              toolTipText={t("QUOTA_UNLIMITED_HINT")}
              {...formikProps}
            />
          </FormRow>

          <FormRow>
            <Input
              required
              title={t("KEY_PAIRS")}
              placeholder="-1"
              fieldName="key_pairs"
              type="number"
              toolTip={true}
              toolTipText={t("QUOTA_UNLIMITED_HINT")}
              {...formikProps}
            />
          </FormRow>

          <FormRow>
            <Input
              required
              title={t("METADATA_ITEMS")}
              placeholder="-1"
              fieldName="metadata_items"
              type="number"
              toolTip={true}
              toolTipText={t("QUOTA_UNLIMITED_HINT")}
              {...formikProps}
            />
          </FormRow>

          <FormRow>
            <Input
              required
              title={t("SERVER_GROUPS")}
              placeholder="-1"
              fieldName="server_groups"
              type="number"
              toolTip={true}
              toolTipText={t("QUOTA_UNLIMITED_HINT")}
              {...formikProps}
            />
          </FormRow>

          <FormRow>
            <Input
              required
              title={t("SERVER_GROUP_MEMBERS")}
              placeholder="-1"
              fieldName="server_group_members"
              type="number"
              toolTip={true}
              toolTipText={t("QUOTA_UNLIMITED_HINT")}
              {...formikProps}
            />
          </FormRow>
        </QuotaGrid>

        <FormRow>
          <Toggle
            value={values.force}
            onChange={() => setFieldValue("force", !values.force)}
            title={t("FORCE_UPDATE")}
            toolTip={true}
            toolTipText={t("FORCE_UPDATE_HINT")}
          />
        </FormRow>

        <FormRow>
          <Toggle
            value={values.set_for_all_regions}
            onChange={() => setFieldValue("set_for_all_regions", !values.set_for_all_regions)}
            title={t("SET_FOR_ALL_REGIONS")}
            toolTip={true}
            toolTipText={t("SET_FOR_ALL_REGIONS_HINT")}
          />
        </FormRow>

        <FormActions>
          <Button
            type="submit"
            variant="primary"
            size="medium"
            title={t("UPDATE_QUOTAS")}
            disabled={updateLoading}
            loading={updateLoading}
          />
        </FormActions>
      </FormSection>
    </TabContent>
  );
};

export default ComputeQuotasTab;
